import API_ROUTES from "../constants/api";
import { ReportPageType, ReportSection } from "../constants/common";
import htmlDocx from 'html-docx-js/dist/html-docx';
import html2pdf from 'html2pdf.js';
import Utils from "./Utils";
import logo from '../../images/Ecodrisil_logo.png'
import { normalizeLanguageCode, getTranslation } from "./TranslationHelpers";
import { buildGhgCoverMetaIconBoxHtml, buildGhgLabelBadgeHtml } from "./ghgWordExportDesign";
import { buildGhgTableOfContentsHtml, escapeTocHtml } from "./reportTocWordHtml";

const GHG_DECIMAL_PLACES_MAX = 15;

/** Colored section title pill in generated GHG report HTML (browser + Word source). */
const GHG_SECTION_HEAD_PILL_PADDING = '12pt 32pt 12pt 22pt';
const GHG_SECTION_HEAD_PILL_RADIUS = '0pt 30pt 30pt 0pt';
const GHG_SECTION_HEAD_PILL_FONT = '18pt';

/**
 * Normalizes company decimal-places setting for GHG tables.
 * @param {unknown} raw
 * @returns {number}
 */
export const normalizeGhgDecimalPlaces = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 2;
  return Math.min(Math.floor(n), GHG_DECIMAL_PLACES_MAX);
};

/**
 * When company report settings include `decimalPlaces`, returns the normalized value.
 * Otherwise returns null so callers can keep legacy formatting (e.g. VSME / older reports).
 * @param {{ decimalPlaces?: number|string|null }} [settings]
 * @returns {number|null}
 */
export const resolveGhgCompanyDecimalPlaces = (settings) => {
  if (!settings || settings.decimalPlaces === undefined || settings.decimalPlaces === null || settings.decimalPlaces === '') {
    return null;
  }
  return normalizeGhgDecimalPlaces(settings.decimalPlaces);
};

/**
 * Formats a template answer for GHG tables using company decimal places and optional UOM.
 * @param {{ value?: unknown, uom?: string }} answer
 * @param {number} decimalPlaces
 */
const formatGhgTableNumericAnswer = (answer, decimalPlaces) => {
  if (!answer) return '';
  if (answer.uom === 'no.') {
    const v = answer.value;
    if (v === null || v === undefined || v === '') return '';
    return String(parseInt(v, 10));
  }
  const rounded = Utils.roundToDecimalPlacesIfNumber(answer.value, decimalPlaces);
  if (rounded === '') return '';
  if (answer.uom) return `${rounded} ${answer.uom}`;
  return rounded;
};

const allowedTypes = ['SINGLE_VALUE', 'TYPE_1_TABLE', 'TYPE_2_TABLE', 'TYPE_3_TABLE', 'PAGE', 'TYPE_5_TABLE', 'TYPE_6_TABLE', 'TYPE_7_TABLE'];

const BASE_URL_DOMAIN = process.env.REACT_APP_ENV === 'esgx'
  ? `${window.location.protocol}//${window.location.host}/esgx`
  : `${window.location.protocol}//${window.location.host}`;


const qAnserData = (answer) => {
  if (!answer) return '';

  const formatValue = (value) => {
    // Convert to string to preserve decimal precision
    const valueStr = String(value);

    // Check if the value contains a decimal point
    if (valueStr.includes('.')) {
      // Split into integer and decimal parts
      const parts = valueStr.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Check if all decimal digits are zeros
      if (decimalPart && /^0+$/.test(decimalPart)) {
        // All decimals are zeros, return just the integer part
        return integerPart;
      }

      // Has meaningful decimals, return as-is (preserving precision)
      return valueStr;
    }

    // No decimal point, return as-is
    return valueStr;
  };

  if (!!answer.uom) {
    if (answer.uom == "no.") {
      return parseInt(answer.value);
    } else {
      const formattedValue = formatValue(answer.value);
      return formattedValue + ' ' + answer.uom;
    }
  }
  return formatValue(answer.value);
}

/**
 * Matches template language code with report language code
 * Handles case-insensitive comparison and matches "EN" with "en-uk", "en-us", etc.
 * @param {string} templateLangCode - Language code from template data (e.g., "EN", "DE", "AR")
 * @param {string} reportLangCode - Language code from report (e.g., "en-uk", "en-us", "de")
 * @returns {boolean} True if codes match
 */
const matchesLanguageCode = (templateLangCode, reportLangCode) => {
  if (!templateLangCode || !reportLangCode) return false;

  // Normalize report language code (e.g., "en-uk" -> "en", "de" -> "de")
  const normalizedReportLang = normalizeLanguageCode(reportLangCode);

  // Extract base language from template code (e.g., "EN" -> "en", "DE" -> "de")
  // Handle both "EN" and "EN-UK" formats
  const templateLangLower = templateLangCode.toLowerCase();
  const baseTemplateLang = templateLangLower.split('-')[0];

  // Compare normalized codes (case-insensitive)
  return baseTemplateLang === normalizedReportLang;
}

const pageHeaderFunction = (templatedata, lang, type) => {
  let findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, 'en'))
  }
  if (!!findLangValue) {
    if (type == 'Social') {
      return (`<h4 style="margin:0 0 18pt 0;padding:0;margin-left:0;padding-left:0;"><span style="font-size: 24px; color: #007bff;"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: Arial, sans-serif;line-height: 1.6;margin:0 0 18pt 0;padding:0;margin-left:0;padding-left:0;">`)
    } else if (type == 'Governance') {
      return (`<h4 style="margin:0 0 18pt 0;padding:0;margin-left:0;padding-left:0;"><span style="font-size: 24px; color: #f7c300;"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: Arial, sans-serif;line-height: 1.6;margin:0 0 18pt 0;padding:0;margin-left:0;padding-left:0;">`)
    } else {
      return (`<h4 style="margin:0 0 18pt 0;padding:0;margin-left:0;padding-left:0;"><span style="font-size: 24px; color: rgb(45, 194, 107);"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: Arial, sans-serif;line-height: 1.6;margin:0 0 18pt 0;padding:0;margin-left:0;padding-left:0;">`)
    }
  } else {
    return ''
  }
}

const pageSectionFunction = (templatedata, lang) => {
  let findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, 'en'))
  }
  if (!!findLangValue) {
    return (`<h5 style='font-size:24px;margin:0px 0px 12px 0px;font-weight: 700;'>${findLangValue.text}</h5>`)
  } else {
    return ''
  }
}

const pageSubSectionFunction = (templatedata, lang, type) => {
  let findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, 'en'))
  }
  if (!!findLangValue) {
    return (`<h6 style='font-size: 20px;margin:0px 0px 16px 0px;font-weight: 600;'>${findLangValue.text}</h6>`)
  } else {
    return ''
  }
}

const pageQuestionFunction = (templatedata, lang) => {
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
  }
  if (!!findLangValue) {
    return (
      `<div style="margin-bottom: 12px;font-size:14px;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>
    <div style="margin-bottom: 16px;font-size:14px;text-align: justify;" class='${templatedata.key}'><span style="color: rgb(0, 0, 0);">${!!templatedata.keyValues && templatedata.keyValues.length > 0 ? qAnserData(templatedata.keyValues[0]) : ""}</span></div>`
    )
  } else {
    return ""
  }
}

const pagetableTYPE_1Function = (templatedata, lang) => {
  var contentHtml = ''
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>`
  }
  contentHtml += `<div style="margin:0 0 18pt 0;padding:0;border: 1px solid #ddd;     border-radius: 6px;     overflow: hidden;">
    <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'>
      <thead><tr style="background-color: rgb(241 247 249); height: 20px;">`
  const columnHeaders = {};
  const filterTopHeader = templatedata.metadata.filter(item => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === "HEADER");
  filterTopHeader.forEach(item => {
    let findLangValue = item.localization.find(val => matchesLanguageCode(val.languageCode, lang))
    if (!findLangValue) {
      findLangValue = item.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
    }
    if (!!findLangValue) {
      contentHtml += `<th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;width:${100 / filterTopHeader.length}%;"><span style="color: rgb(0, 0, 0);">${findLangValue.text}</span></th>`
    }
    columnHeaders[item.columnNumber.replace("1", "")] = item.columnNumber;
  })

  contentHtml += `</tr></thead><tbody>`
  const groupedRows = {};
  templatedata.keyValues.forEach((item) => {
    const col = item?.metadata?.columnNumber?.charAt(0);
    if (!!col) {
      if (!groupedRows[col]) groupedRows[col] = [];
      groupedRows[col].push(item);
    }
  });
  const maxRows = Math.max(...Object.values(groupedRows).map(arr => arr.length), 0);

  for (let i = 0; i < maxRows; i++) {
    contentHtml += `<tr style="height: 20px;">`

    Object.keys(columnHeaders).forEach(col => {
      contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px" class='${groupedRows[col]?.[i]?.key}'><span style="color: rgb(0, 0, 0);">${!!groupedRows[col]?.[i] ? qAnserData(groupedRows[col]?.[i]) : ""}</span></td>`
    });

    contentHtml += `</tr>`
  }
  contentHtml += `</tbody></table></div>`
  return contentHtml
}


const pagetableTYPE_2Function = (templatedata, lang, settings) => {
  const companyDp = resolveGhgCompanyDecimalPlaces(settings);
  var contentHtml = ''
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>`
  }
  contentHtml += `<div style="margin:0 0 18pt 0;padding:0;border: 1px solid #ddd;     border-radius: 6px;     overflow: hidden;">
  <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'><thead><tr style="background-color: rgb(241 247 249); height: 20px;">`
  const columnHeaders = {};
  const filterTopHeader = templatedata.metadata.filter(item => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === "HEADER");
  filterTopHeader.forEach(item => {
    let findLangValue = item.localization.find(val => matchesLanguageCode(val.languageCode, lang))
    if (!findLangValue) {
      findLangValue = item.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
    }
    if (!!findLangValue) {
      contentHtml += `<th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;width:${100 / filterTopHeader.length}%;font-weight:800;font-size:14px;"><span style="color: rgb(0, 0, 0);">${findLangValue.text}</span></th>`
    }
    columnHeaders[item.columnNumber.replace("1", "")] = item.columnNumber;
  })

  contentHtml += `</tr></thead><tbody>`
  const groupedRows = {};
  const filterBody = templatedata.metadata.filter(meta => !/^[A-Z]1$/.test(meta.columnNumber));
  filterBody.forEach((item) => {
    const col = item?.columnNumber?.charAt(0);
    if (!!col) {
      if (!groupedRows[col]) groupedRows[col] = [];
      groupedRows[col].push(item);
    }
  });
  const maxRows = Math.max(...Object.values(groupedRows).map(arr => arr.length), 0);

  // Check if any keyValue has BASE_YEAR dataPeriod
  const hasBaseYearData = templatedata.keyValues && templatedata.keyValues.some(kv => kv.dataPeriod === 'BASE_YEAR' && kv.emissionFactors && kv.emissionFactors.length > 0);

  for (let i = 0; i < maxRows; i++) {
    contentHtml += `<tr style="height: 20px;">`

    Object.keys(columnHeaders).forEach(col => {
      const element = groupedRows[col]?.[i]
      if (!!element) {
        let findLangV = element?.localization.find(val => matchesLanguageCode(val.languageCode, lang))
        if (!findLangV) {
          findLangV = element?.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
        }
        if (element.columnType == "STATIC_TEXT") {
          if (!!findLangV) {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px">${findLangV.text}</td>`
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"></td>`
          }
        } else if (element.columnType == "HEADER") {
          if (!!findLangV) {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px;font-weight:600">${findLangV.text}</td>`
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px;font-weight:600"></td>`
          }
        } else if (element.columnType == "CODE_MAPPING") {
          const findAns = templatedata.keyValues.find(item => item.metadata.columnCode == element.columnCode)
          if (!!findAns) {
            let displayValue;
            if (companyDp != null) {
              displayValue = formatGhgTableNumericAnswer(findAns, companyDp);
            } else if (findAns.dataPeriod === 'BASE_YEAR') {
              displayValue = Utils.roundToDecimalPlacesIfNumber(findAns.value, 4);
            } else {
              displayValue = qAnserData(findAns);
            }
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px" class='${findAns.key}'><span style="color: rgb(0, 0, 0);">${displayValue}</span></td>`
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
          }
        } else if (element.columnType == "BLANK" && hasBaseYearData) {
          // Handle BLANK cells for emission factors when dataPeriod is BASE_YEAR
          // Extract GHG type from columnCode (e.g., "GHGEMF-C2-CO2" -> "CO2")
          // Pattern: GHGEMF-{ColumnNumber}-{GHGType}
          const ghgMatch = element.columnCode ? element.columnCode.match(/-([A-Z]\d+)-([A-Z0-9]+)$/) : null;
          const ghgType = ghgMatch ? ghgMatch[2] : null;

          // Extract row number from columnNumber (e.g., "C2" -> 2, "C3" -> 3, "C4" -> 4)
          const rowNum = parseInt(element.columnNumber.replace(/[A-Z]/, ''));

          // Find the keyValue for this row by matching the row number
          // KeyValues have metadata.columnNumber like "B2", "B3", "B4" which correspond to rows
          const rowKeyValue = templatedata.keyValues.find(kv => {
            if (kv.metadata && kv.metadata.columnNumber) {
              const kvRowNum = parseInt(kv.metadata.columnNumber.replace(/[A-Z]/, ''));
              return kvRowNum === rowNum && kv.dataPeriod === 'BASE_YEAR' && kv.emissionFactors && kv.emissionFactors.length > 0;
            }
            return false;
          });

          if (rowKeyValue && ghgType && rowKeyValue.emissionFactors) {
            // Find the emission factor matching this GHG type
            const emissionFactor = rowKeyValue.emissionFactors.find(ef => ef.ghg === ghgType);
            if (emissionFactor && emissionFactor.factor !== undefined && emissionFactor.factor !== null) {
              const factorValue = companyDp != null
                ? Utils.roundToDecimalPlacesIfNumber(emissionFactor.factor, companyDp)
                : (typeof emissionFactor.factor === 'number'
                  ? emissionFactor.factor.toString()
                  : String(emissionFactor.factor));
              contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">${factorValue}</span></td>`
            } else {
              contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
            }
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
          }
        } else {
          if (!!findLangV) {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">${findLangV.text}</span></td>`
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
          }
        }
      } else {
        contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
      }
    });

    contentHtml += `</tr>`
  }
  contentHtml += `</tbody></table></div>`
  return contentHtml
}

const pagetableTYPE_3Function = (templatedata, lang, settings) => {
  const companyDp = resolveGhgCompanyDecimalPlaces(settings);
  var contentHtml = ''
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>`
  }
  contentHtml += `<div style="margin:0 0 18pt 0;padding:0;border: 1px solid #ddd;     border-radius: 6px;     overflow: hidden;">
  <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'><tbody>`

  // Get all columns and sort them by columnOrder
  const allColumns = templatedata.metadata.sort((a, b) => a.columnOrder - b.columnOrder);

  // Group columns by row number (A1, A2, B1, B2 -> group by 1, 2, etc.)
  const rowGroups = {};
  allColumns.forEach(column => {
    const rowNum = column.columnNumber.replace(/[A-Z]/, ''); // Extract number (1, 2, 3, etc.)
    if (!rowGroups[rowNum]) rowGroups[rowNum] = {};
    rowGroups[rowNum][column.columnNumber] = column;
  });

  // Create rows - one row per row number, sorted by row number
  Object.keys(rowGroups).sort((a, b) => parseInt(a) - parseInt(b)).forEach(rowNum => {
    // Check if this row contains any HEADER type columns
    const hasHeader = Object.values(rowGroups[rowNum]).some(column => column.columnType === "HEADER");
    const rowStyle = hasHeader ? 'height: 20px; background-color: rgb(241, 247, 249);' : 'height: 20px;';
    contentHtml += `<tr style="${rowStyle}">`

    // Get all columns for this row, sorted by column letter (A, B, C, D, etc.)
    const rowColumns = Object.keys(rowGroups[rowNum]).sort((a, b) => a.charCodeAt(0) - b.charCodeAt(0));

    rowColumns.forEach(columnNumber => {
      const column = rowGroups[rowNum][columnNumber];
      let findLangV = column?.localization.find(val => matchesLanguageCode(val.languageCode, lang));
      if (!findLangV) {
        findLangV = column?.localization.find(val => matchesLanguageCode(val.languageCode, 'en'));
      }

      if (column.columnType === "STATIC_TEXT") {
        if (!!findLangV) {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px">${findLangV.text}</td>`
        } else {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px">-</td>`
        }
      } else if (column.columnType === "HEADER") {
        if (!!findLangV) {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px;font-weight:800">${findLangV.text}</td>`
        } else {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px;font-weight:800">-</td>`
        }
      } else if (column.columnType === "CODE_MAPPING") {
        const findAns = templatedata.keyValues.filter(item => {
          // Handle nested structure - check if item has sub-objects with metadata
          let matches = false;

          // Check if item has direct metadata
          if (item.metadata && item.metadata.columnCode === column.columnCode) {
            matches = true;
          }

          // Check if item has nested objects with metadata (like your data structure)
          if (!matches && item[0] && item[0].metadata && item[0].metadata.columnCode === column.columnCode) {
            matches = true;
          }

          return matches;
        });

        if (!!findAns && findAns.length > 0) {
          // For CODE_MAPPING, we should only show the first matching value in this cell
          const element1 = findAns[0]; // Take only the first match for this cell
          let dataForDisplay = element1;
          if (element1[0] && element1[0].value !== undefined) {
            // Use nested data if available
            dataForDisplay = element1[0];
          }

          const mappedText = companyDp != null
            ? formatGhgTableNumericAnswer(dataForDisplay, companyDp)
            : qAnserData(dataForDisplay);
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px" class='${element1.key}'><span style="color: rgb(0, 0, 0);">${mappedText}</span></td>`
        } else {
          // Add empty cell if no mapping found
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
        }
      } else {
        if (!!findLangV) {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">${findLangV.text}</span></td>`
        } else {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
        }
      }
    });

    contentHtml += `</tr>`
  });

  contentHtml += `</tbody></table></div>`
  return contentHtml
}

export function pagetableTYPE_5Function(templatedata, lang, settings) {
  let contentHtml = `<div id="typeTableHolder-${templatedata.key}">`;
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang));
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'));
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style=\"color: rgb(52, 73, 94);\"><strong>${findLangValue.text}</strong></span></div>`;
  }

  // Years from baseYear..currentYear inclusive (descending)
  const baseYear = templatedata.baseYear;
  const currentYear = templatedata.currentYear;
  const years = (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear >= baseYear)
    ? Array.from({ length: currentYear - baseYear + 1 }, (_, i) => currentYear - i)
    : [];
  const type5Dp = resolveGhgCompanyDecimalPlaces(settings) ?? 2;

  contentHtml += `<div style="margin:0 0 18pt 0;padding:0;border: 1px solid #ddd; border-radius: 6px; overflow: hidden;">
  <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'><tbody>`;

  // Header
  const headerColumns = templatedata.metadata
    .filter(item => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === "HEADER")
    .sort((a, b) => a.columnOrder - b.columnOrder);
  contentHtml += `<tr style="background-color: rgb(241, 247, 249); height: 20px;">`;
  headerColumns.forEach(item => {
    let l = item.localization?.find(val => matchesLanguageCode(val.languageCode, lang));
    if (!l) {
      l = item.localization?.find(val => matchesLanguageCode(val.languageCode, 'en'));
    }
    const headerText = l ? l.text : '';
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">${headerText}</span></th>`;
  });
  contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">UOM</span></th>`;
  years.forEach(y => {
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">${y}</span></th>`;
  });
  contentHtml += `</tr>`;

  const codeMappingColumns = templatedata.metadata.filter(item => item.columnType === "CODE_MAPPING");

  // Render per metric as a section
  codeMappingColumns.forEach(metricColumn => {
    const metricData = templatedata.keyValues.find(item => item.metadata.columnCode === metricColumn.columnCode);
    if (!metricData) return;

    if (!metricData.metricId) return;

    // Row with metric name
    contentHtml += `<tr style=\"height: 20px; background-color: rgba(44, 192, 106, 0.08);\">`;
    const colNumber = Utils.getColumnNumber(metricColumn.columnNumber);
    for (let i = 1; i <= headerColumns.length; i++) {
      if (i === colNumber) {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;\">${metricData.metricName || ''}</td>`;
      } else {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
      }
    }
    // UOM data
    contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
    // Year cells
    years.forEach(() => {
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
    });
    contentHtml += `</tr>`;

    // Build unique branches seen anywhere under this metric; order by businessUnits if provided
    const buOrder = (templatedata.businessUnits || []).map(b => ({ id: b.id, name: b.name }));
    const branchMap = new Map();
    (metricData.periodData || []).forEach(p => (p.branchData || []).forEach(b => branchMap.set(b.branchId || b.branchName, b.branchName)));
    (metricData.datapoints || []).sort((a, b) => a.sortOrder - b.sortOrder).forEach(dp => (dp.periodData || []).forEach(p => (p.branchData || []).forEach(b => branchMap.set(b.branchId || b.branchName, b.branchName))));
    let branches = Array.from(branchMap.entries()).map(([key, name]) => ({ key, name }));
    if (buOrder.length > 0) {
      const indexOf = (br) => {
        const i = buOrder.findIndex(b => b.id === br.key || b.name === br.name);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
      }
      branches.sort((a, b) => indexOf(a) - indexOf(b));
    } else {
      branches.sort((a, b) => a.name.localeCompare(b.name));
    }

    branches.forEach(br => {
      const branchName = br.name;
      // Branch aggregate row (from metric totals branchData)
      contentHtml += `<tr style=\"height: 20px;\">`;
      for (let i = 1; i <= headerColumns.length; i++) {
        if (i === colNumber) {
          contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;\">${branchName}</td>`;
        } else {
          contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
        }
      }
      // Empty cell for UOM
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);padding: 10px; text-align: left;font-size:14px;font-weight:600;\">${metricData.unitOfMeasure}</td>`;
      years.forEach(y => {
        const p = (metricData.periodData || []).find(pp => new Date(pp.period).getFullYear() === y);
        const v = p ? (p.branchData || []).find(b => (b.branchId === br.key) || (b.branchName === branchName)) : null;
        let metricValue = v?.value ?? ''
        if (metricData.emissionFactorLinked) {
          metricValue = v?.emissionValue ? (v?.emissionValue / 1000) : '';
        } else if (metricData.emission) {
          metricValue = v?.solution ? (v?.solution / 1000) : '';
        }
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${Utils.roundToDecimalPlacesIfNumber(metricValue, type5Dp)}</td>`;
      });
      contentHtml += `</tr>`;

      // Datapoints under this branch
      (metricData.datapoints || []).forEach(dp => {
        contentHtml += `<tr style=\"height: 20px;\">`;
        for (let i = 1; i <= headerColumns.length; i++) {
          if (i === colNumber) {
            contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;\">${dp.datapointName}</td>`;
          } else {
            contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
          }
        }
        // UOM data
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;\">${dp.unitOfMeasure}</td>`;
        //Year data
        years.forEach(y => {
          const p = (dp.periodData || []).find(pp => new Date(pp.period).getFullYear() === y);
          const v = p ? (p.branchData || []).find(b => (b.branchId === br.key) || (b.branchName === branchName)) : null;
          let dpValueToUse = null;
          if (dp.emission) {
            dpValueToUse = v?.emissionValue ? (v?.emissionValue / 1000) : '';
          } else {
            dpValueToUse = v?.value ?? '';
          }
          contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${Utils.roundToDecimalPlacesIfNumber(dpValueToUse, type5Dp)}</td>`;
        });
        contentHtml += `</tr>`;
      });
    });
  });
  contentHtml += `</tbody></table></div></div>`;
  return contentHtml;
}

/**
 * 
 * Table data with base year to current year
 * 
 * @param {Object} templatedata - The template data object
 * @param {string} lang - The language code
 * @param {Object} settings - The settings object
 * @returns {string} - The HTML string for the table
 */

export function pagetableTYPE_6Function(templatedata, lang, settings) {
  let contentHtml = `<div id="typeTableHolder-${templatedata.key}">`;
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang));
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'));
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style=\"color: rgb(52, 73, 94);\"><strong>${findLangValue.text}</strong></span></div>`;
  }

  // Years from baseYear..currentYear inclusive (ascending order)
  const baseYear = templatedata.baseYear;
  const currentYear = templatedata.currentYear;
  const years = (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear >= baseYear)
    ? Array.from({ length: currentYear - baseYear + 1 }, (_, i) => baseYear + i)
    : (typeof currentYear === 'number' ? [currentYear] : []); // Fallback to currentYear only if baseYear missing
  const type6FractionDigits = resolveGhgCompanyDecimalPlaces(settings) ?? 4;

  contentHtml += `<div style="margin:0 0 18pt 0;padding:0;border: 1px solid #ddd; border-radius: 6px; overflow: hidden;">
  <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'><tbody>`;

  // Header
  const headerColumns = templatedata.metadata
    .filter(item => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === "HEADER")
    .sort((a, b) => a.columnOrder - b.columnOrder);
  contentHtml += `<tr style="background-color: rgb(241, 247, 249); height: 20px;">`;
  headerColumns.forEach(item => {
    let l = item.localization?.find(val => matchesLanguageCode(val.languageCode, lang));
    if (!l) {
      l = item.localization?.find(val => matchesLanguageCode(val.languageCode, 'en'));
    }
    const headerText = l ? l.text : '';
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">${headerText}</span></th>`;
  });

  // Base Year column header
  if (typeof baseYear === 'number') {
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">Base Year (${baseYear})</span></th>`;
  }

  // Year columns (from baseYear+1 to currentYear)
  years.filter(y => y !== baseYear).forEach(y => {
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">${y}</span></th>`;
  });

  // % Change column header
  if (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear > baseYear) {
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">% Change (${baseYear} → ${currentYear})</span></th>`;
  }

  contentHtml += `</tr>`;

  // Get all metrics (keyValues with CODE_MAPPING type and metricId)
  // Deduplicate by metricId to avoid showing the same metric multiple times
  const metricsMap = new Map();
  templatedata.keyValues.forEach(item => {
    if (item.metadata &&
      item.metadata.columnType === "CODE_MAPPING" &&
      item.metricId) {
      // If metricId already exists, keep the one with lower sortOrder or first occurrence
      if (!metricsMap.has(item.metricId) ||
        (item.sortOrder || 0) < (metricsMap.get(item.metricId).sortOrder || 0)) {
        metricsMap.set(item.metricId, item);
      }
    }
  });
  const metrics = Array.from(metricsMap.values())
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)); // Sort by sortOrder if available

  // Store values for Total row calculation
  const totalValuesByYear = {};
  const allYears = baseYear !== undefined ? [baseYear, ...years.filter(y => y !== baseYear)] : years;

  // Render per metric - company-level only (no branches, no datapoints)
  metrics.forEach(metricData => {
    // Get the column number from metadata
    const metricColumn = templatedata.metadata.find(item => item.columnCode === metricData.metadata.columnCode);
    const colNumber = metricColumn ? Utils.getColumnNumber(metricColumn.columnNumber) : 1;

    // Single row per metric with company-level values
    contentHtml += `<tr style=\"height: 20px;\">`;
    for (let i = 1; i <= headerColumns.length; i++) {
      if (i === colNumber) {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;\">${metricData.metricName || ''}</td>`;
      } else {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
      }
    }

    // Collect values for this metric across all years
    const metricValuesByYear = {};

    // Base Year column
    if (typeof baseYear === 'number') {
      const periodData = (metricData.periodData || []).find(p => new Date(p.period).getFullYear() === baseYear);
      let metricValue = '';

      if (periodData) {
        if (metricData.emissionFactorLinked && periodData.emissionValue !== undefined && periodData.emissionValue !== null && periodData.emissionValue !== 0) {
          metricValue = periodData.emissionValue / 1000;
        } else if (metricData.emission && periodData.emissionValue !== undefined && periodData.emissionValue !== null && periodData.emissionValue !== 0) {
          metricValue = periodData.emissionValue / 1000;
        } else if (periodData.totalValue !== undefined && periodData.totalValue !== null) {
          metricValue = periodData.totalValue;
        } else if (periodData.value !== undefined && periodData.value !== null) {
          metricValue = periodData.value;
        }
      }

      metricValuesByYear[baseYear] = parseFloat(metricValue) || 0;
      totalValuesByYear[baseYear] = (totalValuesByYear[baseYear] || 0) + metricValuesByYear[baseYear];
      const displayBase = (metricValue !== '' && metricValue !== null && metricValue !== undefined) ? Utils.formatNumberWithThousandSeparators(metricValue, type6FractionDigits) : '';
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${displayBase}</td>`;
    }

    // Year columns (from baseYear+1 to currentYear)
    years.filter(y => y !== baseYear).forEach(y => {
      const periodData = (metricData.periodData || []).find(p => new Date(p.period).getFullYear() === y);
      let metricValue = '';

      if (periodData) {
        if (metricData.emissionFactorLinked && periodData.emissionValue !== undefined && periodData.emissionValue !== null && periodData.emissionValue !== 0) {
          metricValue = periodData.emissionValue / 1000;
        } else if (metricData.emission && periodData.emissionValue !== undefined && periodData.emissionValue !== null && periodData.emissionValue !== 0) {
          metricValue = periodData.emissionValue / 1000;
        } else if (periodData.totalValue !== undefined && periodData.totalValue !== null) {
          metricValue = periodData.totalValue;
        } else if (periodData.value !== undefined && periodData.value !== null) {
          metricValue = periodData.value;
        }
      }

      metricValuesByYear[y] = parseFloat(metricValue) || 0;
      totalValuesByYear[y] = (totalValuesByYear[y] || 0) + metricValuesByYear[y];
      const displayYear = (metricValue !== '' && metricValue !== null && metricValue !== undefined) ? Utils.formatNumberWithThousandSeparators(metricValue, type6FractionDigits) : '';
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${displayYear}</td>`;
    });

    // % Change column
    if (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear > baseYear) {
      const baseValue = metricValuesByYear[baseYear] || 0;
      const currentValue = metricValuesByYear[currentYear] || 0;
      let percentChange = '';

      if (baseValue !== 0) {
        const change = ((currentValue - baseValue) / baseValue) * 100;
        percentChange = Utils.formatNumberWithThousandSeparators(change, type6FractionDigits);
      } else if (currentValue !== 0) {
        percentChange = 'N/A'; // Can't calculate % change when base is 0
      } else {
        percentChange = '0';
      }

      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${percentChange}</td>`;
    }

    contentHtml += `</tr>`;
  });

  // Total row
  if (metrics.length > 0) {
    contentHtml += `<tr style=\"height: 20px; background-color: rgba(44, 192, 106, 0.08);\">`;
    const firstHeaderCol = headerColumns[0];
    const colNumber = firstHeaderCol ? Utils.getColumnNumber(firstHeaderCol.columnNumber) : 1;

    for (let i = 1; i <= headerColumns.length; i++) {
      if (i === colNumber) {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;\">Total</td>`;
      } else {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
      }
    }

    // Base Year total
    if (typeof baseYear === 'number') {
      const totalValue = totalValuesByYear[baseYear] || 0;
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;font-weight:600;\">${Utils.formatNumberWithThousandSeparators(totalValue, type6FractionDigits)}</td>`;
    }

    // Year totals (from baseYear+1 to currentYear)
    years.filter(y => y !== baseYear).forEach(y => {
      const totalValue = totalValuesByYear[y] || 0;
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;font-weight:600;\">${Utils.formatNumberWithThousandSeparators(totalValue, type6FractionDigits)}</td>`;
    });

    // Total % Change
    if (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear > baseYear) {
      const baseTotal = totalValuesByYear[baseYear] || 0;
      const currentTotal = totalValuesByYear[currentYear] || 0;
      let percentChange = '';

      if (baseTotal !== 0) {
        const change = ((currentTotal - baseTotal) / baseTotal) * 100;
        percentChange = Utils.formatNumberWithThousandSeparators(change, type6FractionDigits);
      } else if (currentTotal !== 0) {
        percentChange = 'N/A';
      } else {
        percentChange = '0';
      }

      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;font-weight:600;\">${percentChange}</td>`;
    }

    contentHtml += `</tr>`;
  }

  contentHtml += `</tbody></table></div></div>`;
  return contentHtml;
}


/**
 * Renders TYPE_7_TABLE (Table Qualitative): same structure as Type 5 (metric, entity, rows)
 * but with only one data column: after each question the answer shows.
 * API shape: element.keyValues[] with metadata.columnType === 'CODE_MAPPING' each have
 * branchQualitativeData: [{ branchId, branchName, questionAnswers }].
 */
export function pagetableTYPE_7Function(templatedata, lang, settings) {
  const escapeHtml = (s) => {
    if (s == null || s === '') return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };
  const escapeHtmlText = (s) => (s == null || s === '' ? '-' : escapeHtml(s));
  const nl2br = (s) => (s == null ? '' : String(s).replace(/\n/g, '<br/>'));

  let contentHtml = `<div id="typeTableHolder-${templatedata.key}">`;
  let findLangValue = templatedata.localization?.find(val => matchesLanguageCode(val.languageCode, lang));
  if (!findLangValue && templatedata.localization?.length) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'));
  }
  if (!!findLangValue && findLangValue.text) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style="color: rgb(52, 73, 94);"><strong>${escapeHtmlText(findLangValue.text)}</strong></span></div>`;
  }

  const getAnswerDisplay = (qa) => {
    if (qa.combinedAnswer != null && qa.combinedAnswer !== '') {
      return nl2br(escapeHtml(qa.combinedAnswer));
    }
    const byMonth = qa.answersByMonth;
    if (byMonth && typeof byMonth === 'object' && Object.keys(byMonth).length > 0) {
      const parts = Object.entries(byMonth)
        .map(([month, text]) => (text != null && text !== '' ? `${month}: ${text}` : null))
        .filter(Boolean);
      return parts.length > 0 ? nl2br(escapeHtml(parts.join('\n'))) : '-';
    }
    return '-';
  };

  const sortByOrder = (arr) => {
    if (!arr || !Array.isArray(arr)) return [];
    return [...arr].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  };

  const headerColumns = (templatedata.metadata || [])
    .filter((item) => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === 'HEADER')
    .sort((a, b) => (a.columnOrder || 0) - (b.columnOrder || 0));
  const codeMappingColumns = (templatedata.metadata || []).filter((item) => item.columnType === 'CODE_MAPPING');
  const buOrder = (templatedata.businessUnits || []).map((b) => ({ id: b.id, name: b.name }));

  const orderBranches = (branchList) => {
    if (!branchList || branchList.length === 0) return [];
    if (buOrder.length > 0) {
      const indexOf = (br) => {
        const i = buOrder.findIndex((b) => b.id === br.branchId || b.name === br.branchName);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
      };
      return [...branchList].sort((a, b) => indexOf(a) - indexOf(b));
    }
    return [...branchList].sort((a, b) => (a.branchName || '').localeCompare(b.branchName || ''));
  };

  const headerText =
    headerColumns.length > 0
      ? (() => {
        const item = headerColumns[0];
        let l = item.localization?.find((val) => matchesLanguageCode(val.languageCode, lang));
        if (!l && item.localization?.length) l = item.localization.find((val) => matchesLanguageCode(val.languageCode, 'en'));
        return l ? l.text : 'Questions';
      })()
      : 'Questions';

  contentHtml += `<div style="margin:0 0 18pt 0;padding:0;border: 1px solid #ddd; border-radius: 6px; overflow: hidden;">
  <table style="width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;"><tbody>`;
  contentHtml += `<tr style="background-color: rgb(241, 247, 249); height: 20px;">`;
  contentHtml += `<th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-weight:800;font-size:14px;"><span style="color: rgb(0, 0, 0);">${escapeHtml(headerText)}</span></th>`;
  contentHtml += `</tr>`;

  codeMappingColumns.forEach((metricColumn) => {
    const metricData = (templatedata.keyValues || []).find((item) => item.metadata && item.metadata.columnCode === metricColumn.columnCode);
    if (!metricData) return;

    const metricName = metricData.metricName || metricColumn.columnCode || '';

    contentHtml += `<tr style="height: 20px; background-color: rgba(44, 192, 106, 0.08);">`;
    contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;">${escapeHtml(metricName)}</td>`;
    contentHtml += `</tr>`;

    const branchData = metricData.branchQualitativeData || [];
    const orderedBranches = orderBranches(branchData);
    let metricHasAnyShownBranch = false;

    orderedBranches.forEach((br) => {
      const questionAnswers = br.questionAnswers || [];
      const hasAnyChildQuestions = questionAnswers.some((qa) => (qa.children || []).length > 0);
      if (!hasAnyChildQuestions) return;

      metricHasAnyShownBranch = true;
      const branchName = br.branchName != null ? br.branchName : (br.branchId != null ? String(br.branchId) : '-');
      contentHtml += `<tr style="height: 20px;">`;
      contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;">${escapeHtml(branchName)}</td>`;
      contentHtml += `</tr>`;

      const questionPadding = 10;
      const answerPadding = 10;
      questionAnswers.forEach((qa) => {
        const children = (qa.children || []);
        sortByOrder(children).forEach((child) => {
          const questionText = child.questionText != null ? nl2br(escapeHtml(child.questionText)) : '-';
          const answerHtml = getAnswerDisplay(child);
          const answerDisplay = answerHtml && answerHtml.trim() !== '' ? answerHtml : '-';
          contentHtml += `<tr style="height: 20px;">`;
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px 10px 10px ${questionPadding}px; text-align: left;font-size:14px;"><strong><em>${questionText}</em></strong></td>`;
          contentHtml += `</tr>`;
          contentHtml += `<tr style="height: 20px;">`;
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px 10px 10px ${answerPadding}px; text-align: left;font-size:14px;"><em>${answerDisplay}</em></td>`;
          contentHtml += `</tr>`;
        });
      });
    });

    if (!metricHasAnyShownBranch) {
      contentHtml += `<tr style="height: 20px;">`;
      contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;">-</td>`;
      contentHtml += `</tr>`;
    }
  });

  contentHtml += `</tbody></table></div></div>`;
  return contentHtml;
}

const pageHtml = (templateData, lang, type, settings) => {
  if (!!templateData && templateData.length > 0) {
    var htmlContent = ''
    for (let index = 0; index < templateData.length; index++) {
      const element = templateData[index];
      if (element.keyValueType == "PAGE") {
        htmlContent += pageHeaderFunction(element, lang, type)
      }
      if (element.keyValueType == "SECTION") {
        htmlContent += pageSectionFunction(element, lang)
      }
      if (element.keyValueType == "SUB_SECTION") {
        htmlContent += pageSubSectionFunction(element, lang, type)
      }
      if (element.keyValueType == "SINGLE_VALUE") {
        if (!!element.keyValues && element.keyValues.length > 0 && !!qAnserData(element.keyValues[0])) {
          htmlContent += pageQuestionFunction(element, lang)
        }
      }
      if (element.keyValueType == "TYPE_1_TABLE") {
        htmlContent += pagetableTYPE_1Function(element, lang)
      }
      if (element.keyValueType == "TYPE_2_TABLE") {
        htmlContent += pagetableTYPE_2Function(element, lang, settings)
      }
      if (element.keyValueType == "TYPE_3_TABLE") {
        htmlContent += pagetableTYPE_3Function(element, lang, settings)
      }
      if (element.keyValueType == "TYPE_5_TABLE") {
        htmlContent += pagetableTYPE_5Function(element, lang, settings)
      }
      if (element.keyValueType == "TYPE_6_TABLE") {
        htmlContent += pagetableTYPE_6Function(element, lang, settings)
      }
      if (element.keyValueType == "TYPE_7_TABLE") {
        htmlContent += pagetableTYPE_7Function(element, lang, settings)
      }
    }
    htmlContent += `</div>`
    return htmlContent
  } else {
    return `<div></div>`
  }

}

/**
 * Modern GHG section-overview page (Environment / Social / Governance / General Info).
 * Word-safe (html-docx-js): table-based layout, inline styles, bgcolor fallbacks.
 * Matches the cover + TOC design language: thin accent bar, micro-caps eyebrow,
 * large title, numbered-badge row list, and blue/amber closing accent bar.
 *
 * @param {{
 *   t: (key: string) => string,
 *   accentColor: string,
 *   sectionTitle: string,
 *   items: string[]
 * }} opts
 */
function buildGhgSectionOverviewHtml({ t, accentColor, sectionTitle, items }) {
  const PRIMARY_BLUE = '#1d4ed8';
  const ACCENT_YELLOW = '#f59e0b';
  const MUTED_GREY = '#94a3b8';
  const DIVIDER = '#e2e8f0';
  const CARD_BORDER = '#e5e7eb';
  const TITLE_BLACK = '#0a0a0a';
  const BODY_DARK = '#1e293b';

  // Pair the bottom accent bar with amber by default; if section accent IS amber-ish, pair with blue.
  const accentLower = (accentColor || '').toLowerCase();
  const isAmberish =
    accentLower === ACCENT_YELLOW.toLowerCase() ||
    accentLower === '#f7c300' ||
    accentLower === '#eab308' ||
    accentLower === '#fbbf24';
  const secondaryColor = isAmberish ? PRIMARY_BLUE : ACCENT_YELLOW;

  const safeItems = (items || []).filter((label) => label != null && String(label).trim() !== '');

  const rows = safeItems
    .map((label, idx) => {
      const isLast = idx === safeItems.length - 1;
      const borderBottom = isLast ? '' : `border-bottom:solid 1pt ${DIVIDER};`;
      const num = String(idx + 1).padStart(2, '0');
      return `<tr><td style="padding:14pt 0;vertical-align:middle;${borderBottom}border-left:none;border-right:none;border-top:none;mso-border-alt:none;">
<table cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100% !important;border-collapse:collapse;table-layout:fixed;border:none;mso-border-alt:none;"><tr>
<td style="width:52pt;vertical-align:middle;padding:0 14pt 0 0;border:none;mso-border-alt:none;">
<table width="40" height="40" cellpadding="0" cellspacing="0" border="0" style="width:40pt !important;max-width:40pt !important;height:40pt !important;border-collapse:collapse;border:none;mso-border-alt:none;"><tr><td align="center" valign="middle" bgcolor="${accentColor}" style="background-color:${accentColor};color:#ffffff;font-weight:800;font-size:12pt;mso-line-height-rule:exactly;padding:0;line-height:1;letter-spacing:0.4pt;border-radius:9pt;-webkit-border-radius:9pt;-moz-border-radius:9pt;width:40pt;height:40pt;font-family:'Inter',Arial,sans-serif;">${num}</td></tr></table>
</td>
<td valign="middle" style="border:none;mso-border-alt:none;padding:0;">
<p style="margin:0 0 18pt 0;padding:0;font-size:12.5pt;font-weight:700;color:#0f172a;line-height:1.3;mso-line-height-rule:exactly;">${label}</p>
</td>
</tr></table>
</td></tr>`;
    })
    .join('');

  return `<div class="ghg-section-overview" style="page-break-after:always;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;background-color:#ffffff;color:${BODY_DARK};">
<div style="padding:54pt 56pt 44pt 56pt;margin:0 auto;width:100%;max-width:8.27in;background-color:#ffffff;border:1px solid ${CARD_BORDER};border-radius:12pt;-webkit-border-radius:12pt;-moz-border-radius:12pt;box-sizing:border-box;mso-line-height-rule:exactly;">

<p style="margin:0 0 8pt 0;padding:0;font-size:9.5pt;font-weight:700;color:${MUTED_GREY};letter-spacing:3pt;text-transform:uppercase;line-height:1.2;mso-line-height-rule:exactly;">${t('Overview.label')}</p>
<h2 style="font-size:38pt;font-weight:800;margin:0 0 18pt 0;padding:0;color:${TITLE_BLACK};line-height:1.05;mso-line-height-rule:exactly;letter-spacing:-0.8pt;">${sectionTitle}</h2>
<table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;margin:24pt 0 6pt 0;">
<tr><td style="height:1pt;line-height:1pt;font-size:1pt;border-top:solid 1pt ${DIVIDER};">&nbsp;</td></tr>
</table>
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100% !important;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;border:none;mso-border-alt:none;table-layout:fixed;">${rows}</table>
<table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;margin:28pt 0 0 0;background-image:linear-gradient(90deg, ${accentColor} 0%, ${accentColor} 55%, ${secondaryColor} 70%, ${secondaryColor} 100%);">
<tr>
<td bgcolor="${accentColor}" style="width:60%;height:3pt;line-height:3pt;font-size:1pt;background-color:${accentColor};mso-line-height-rule:exactly;">&nbsp;</td>
<td bgcolor="${secondaryColor}" style="width:40%;height:3pt;line-height:3pt;font-size:1pt;background-color:${secondaryColor};mso-line-height-rule:exactly;">&nbsp;</td>
</tr>
</table>
</div>
</div>`;
}

export default class GHGReportingUtils {


  static getCoverSection(companyId, year, t, reportData = {}, language, i18n) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);

    const logoURL = API_ROUTES.PROFILESETTINGS.GETCOMPANYLOGO.replace(
      "<companyId>",
      companyId
    );

    // Extract report metadata
    const version = reportData.version || "1";
    const generatedBy = reportData.generatedBy || reportData.companyName || "System";
    const currentDate = reportData.date || new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');

    const versionLabel = getTranslation(i18n, "Version.label", language);
    const generatedByLabel = getTranslation(i18n, "GeneratedBy.label", language);
    const dateLabel = getTranslation(i18n, "Date.label", language);

    const PRIMARY_BLUE = "#1d4ed8";
    const ACCENT_YELLOW = "#f59e0b";
    const TITLE_BLACK = "#0a0a0a";
    const MUTED_GREY = "#94a3b8";
    const BODY_DARK = "#1e293b";
    const CARD_BORDER = "#e5e7eb";

    const ghgLabelEscaped = escapeTocHtml(getTranslation(i18n, "GHG.label", language));
    const ghgCoverBadgeHtml = buildGhgLabelBadgeHtml(ghgLabelEscaped, PRIMARY_BLUE, "cover");

    const renderMetaCard = (iconType, iconBg, label, value) => `
              <table cellpadding="0" cellspacing="0" border="0" class="ghg-cover-meta-card" style="border-collapse:collapse;">
                <tr>
                  <td style="width:38pt;vertical-align:middle;padding:0 10pt 0 0;border:none;mso-border-alt:none;">
                    ${buildGhgCoverMetaIconBoxHtml(iconType, iconBg)}
                  </td>
                  <td style="vertical-align:middle;padding:0;border:none;mso-border-alt:none;">
                    <div style="font-size:7.5pt;font-weight:700;color:${MUTED_GREY};letter-spacing:1.6pt;text-transform:uppercase;line-height:1.2;mso-line-height-rule:exactly;">${label}</div>
                    <div style="font-size:10.5pt;font-weight:700;color:${BODY_DARK};line-height:1.3;padding-top:3pt;mso-line-height-rule:exactly;">${value}</div>
                  </td>
                </tr>
              </table>`;

    return {
      name: getTranslation(i18n, "FrontCoverpage.label", language),
      index: 0,
      sortOrder: 1,
      type: ReportSection.TITLE,
      pages: [{
        name: getTranslation(i18n, "FrontCoverpage.label", language),
        sortOrder: 1,
        index: 0,
        type: ReportPageType.TITLE,
        html: `<div class="ghg-cover-page" style="page-break-after:always;background-color:#ffffff;border:1px solid ${CARD_BORDER};border-radius:12pt;-webkit-border-radius:12pt;-moz-border-radius:12pt;width:8.27in;min-height:11.69in;margin:auto;box-sizing:border-box;padding:54pt 60pt 46pt 60pt;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:${BODY_DARK};">
          <table class="ghg-cover-layout-table" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;min-height:10.4in;border:none;mso-border-alt:none;">
            <tr>
              <td style="vertical-align:top;text-align:right;padding:0 0 70pt 0;">
                <img class="ghg-cover-logo" src="${logoURL}" alt="" style="height:28pt;width:auto;max-height:28pt;display:inline-block;border:0;"/>
              </td>
            </tr>
            <tr>
              <td style="vertical-align:top;padding:0 0 5pt 0;">
                ${ghgCoverBadgeHtml}
              </td>
            </tr>
        
            <tr>
              <td style="vertical-align:top;padding:0 0 18pt 0;">
                <span style="font-size:9.5pt;font-weight:700;color:${MUTED_GREY};letter-spacing:3pt;text-transform:uppercase;">
                  ${getTranslation(i18n, "ReportingStandards.label", language)}
                </span>
              </td>
            </tr>
            <tr>
              <td style="vertical-align:top;padding:0;">
                <h1 style="font-size:36pt;font-weight:800;color:${PRIMARY_BLUE};margin:0 0 18pt 0;padding:0;line-height:1.08;mso-line-height-rule:exactly;letter-spacing:-0.5pt;">
                  ${getTranslation(i18n, "GHGReport.GHGFull.label", language)}
                </h1>
              </td>
            </tr>
            <tr>
              <td style="vertical-align:top;padding:6pt 0 0 0;">
                <h2 style="font-size:52pt;font-weight:900;color:${TITLE_BLACK};margin:0 0 18pt 0;padding:0;line-height:1.02;mso-line-height-rule:exactly;letter-spacing:-1.5pt;">
                  ${year}
                </h2>
              </td>
            </tr>
            <tr>
              <td style="vertical-align:top;height:230pt;line-height:230pt;font-size:1pt;">&nbsp;</td>
            </tr>
            <tr>
              <td style="vertical-align:bottom;padding:0 0 26pt 0;">
                <table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;background-image:linear-gradient(90deg, ${PRIMARY_BLUE} 0%, ${PRIMARY_BLUE} 55%, ${ACCENT_YELLOW} 70%, ${ACCENT_YELLOW} 100%);">
                  <tr>
                    <td bgcolor="${PRIMARY_BLUE}" style="width:60%;height:3pt;line-height:3pt;font-size:1pt;background-color:${PRIMARY_BLUE};mso-line-height-rule:exactly;">&nbsp;</td>
                    <td bgcolor="${ACCENT_YELLOW}" style="width:40%;height:3pt;line-height:3pt;font-size:1pt;background-color:${ACCENT_YELLOW};mso-line-height-rule:exactly;">&nbsp;</td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td style="vertical-align:bottom;padding:0;">
                <table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;">
                  <tr>
                    <td style="width:30%;vertical-align:middle;padding:0 10pt 0 0;">
                      ${renderMetaCard('doc', PRIMARY_BLUE, versionLabel, version)}
                    </td>
                    <td style="width:40%;vertical-align:middle;padding:0 10pt;">
                      ${renderMetaCard('user', PRIMARY_BLUE, generatedByLabel, generatedBy)}
                    </td>
                    <td style="width:30%;vertical-align:middle;padding:0 0 0 10pt;">
                      ${renderMetaCard('calendar', ACCENT_YELLOW, dateLabel, currentDate)}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </div>`,
      }]
    }
  }

  static getTableOfContentsSection(t, language, i18n) {
    const tocHtml = buildGhgTableOfContentsHtml((key) =>
      escapeTocHtml(getTranslation(i18n, key, language)),
    );

    return {
      name: getTranslation(i18n, "TableofContents.label", language),
      index: 1,
      sortOrder: 2,
      type: ReportSection.CONTENTS,
      pages: [{
        name: getTranslation(i18n, "TableofContents.label", language),
        sortOrder: 1,
        index: 0,
        type: ReportPageType.CONTENTS,
        html: tocHtml,
      }]
    };
  }

  static getGeneralInfoSection(b1, language, t) {
    const pages = [
      {
        name: t('Overview.label'),
        index: 0,
        sortOrder: 1,
        type: ReportPageType.GENERAL_INFO_OVERVIEW,
        html: buildGhgSectionOverviewHtml({
          t,
          accentColor: '#f59e0b',
          sectionTitle: t('VSMEReport.GeneralInfo'),
          items: [
            t('VSMEReport.B1'),
            t('VSMEReport.B2'),
            t('VSMEReport.C1'),
            t('VSMEReport.C2'),
          ],
        }),
      }];
    if (b1.length > 0 && b1.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      pages.push({
        name: t('VSMEReport.B1'),
        index: 1,
        sortOrder: 2,
        type: ReportPageType.B1,
        html: pageHtml(b1, language, 'GeneralInfo'),
      })
    }
    return {
      name: t('VSMEReport.GeneralInfo'),
      index: 6,
      sortOrder: 7,
      type: ReportSection.GENERAL_INFO,
      pages: pages
    }
  }

  static getEnvironmentSection(b2, language, t) {
    const pages = [{
      name: t('Overview.label'),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.ENV_OVERVIEW,
      html: buildGhgSectionOverviewHtml({
        t,
        accentColor: '#059669',
        sectionTitle: t('Environment.label'),
        items: [
          t('VSMEReport.B3'),
          t('VSMEReport.B4'),
          t('VSMEReport.B5'),
          t('VSMEReport.B6'),
          t('VSMEReport.B7'),
          t('VSMEReport.C3'),
          t('VSMEReport.C4'),
        ],
      }),
    }]
    if (b2.length > 0 && b2.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      pages.push({
        name: t('VSMEReport.B2'),
        index: 2,
        sortOrder: 3,
        type: ReportPageType.B2,
        html: pageHtml(b2, language, 'Environment'),
      })
    }

    return {
      name: t('Environment.label'),
      index: 7,
      sortOrder: 8,
      type: ReportSection.ENVIRONMENT,
      pages: pages
    }
  }

  static getSocialSection(b3, language, t) {
    const pages = [{
      name: t('Overview.label'),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.SOCIAL_OVERVIEW,
      html: buildGhgSectionOverviewHtml({
        t,
        accentColor: '#2563eb',
        sectionTitle: t('Social.label'),
        items: [
          t('VSMEReport.B8'),
          t('VSMEReport.B9'),
          t('VSMEReport.B10'),
          t('VSMEReport.C5'),
          t('VSMEReport.C6'),
          t('VSMEReport.C7'),
        ],
      }),
    }]
    if (b3.length > 0 && b3.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      pages.push({
        name: t('VSMEReport.B3'),
        index: 1,
        sortOrder: 2,
        type: ReportPageType.B3,
        html: pageHtml(b3, language, 'Social'),
      })
    }

    return {
      name: t('Social.label'),
      index: 9,
      sortOrder: 10,
      type: ReportSection.SOCIAL,
      pages: pages
    }
  }

  static getGovernanceSection(b4, b5, language, t) {
    const pages = [{
      name: t('Governance.label'),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.GOV_OVERVIEW,
      html: buildGhgSectionOverviewHtml({
        t,
        accentColor: '#6366f1',
        sectionTitle: t('Governance.label'),
        items: [
          t('VSMEReport.B8'),
        ],
      }),
    }]

    if (b4.length > 0 && b4.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      pages.push({
        name: t('VSMEReport.B4'),
        index: 2,
        sortOrder: 3,
        type: ReportPageType.B4,
        html: pageHtml(b4, language, 'Governance'),
      })
    }
    if (b5.length > 0 && b5.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      pages.push({
        name: t('VSMEReport.B5'),
        index: 3,
        sortOrder: 4,
        type: ReportPageType.B5,
        html: pageHtml(b5, language, 'Governance'),
      })
    }
    return {
      name: t('Governance.label'),
      index: 9,
      sortOrder: 10,
      type: ReportSection.GOVERNANCE,
      pages: pages
    }
  }

  static getExecutiveSummarySection(b1Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);

    let html = '';
    if (b1Data.length > 0 && b1Data.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b1Data, normalizedLang, getTranslation(i18n, 'GHGReport.ExecutiveSummary.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.OverviewHighlights.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.OVERVIEW_HIGHLIGHTS,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
          <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #007bff;padding: ${GHG_SECTION_HEAD_PILL_PADDING};  border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.ExecutiveSummary.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #007bff;padding: ${GHG_SECTION_HEAD_PILL_PADDING};border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.ExecutiveSummary.label', language)}</h3>
          </div>
        </div>
        <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.OverviewHighlights.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.OverviewHighlightsContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.ExecutiveSummary.label', language),
      index: 2,
      sortOrder: 3,
      type: ReportSection.EXECUTIVE_SUMMARY,
      pages: pages
    }
  }

  static getAboutReportSection(b2Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);

    let html = '';
    if (b2Data.length > 0 && b2Data.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b2Data, normalizedLang, getTranslation(i18n, 'GHGReport.AboutReport.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.ReportBasisScope.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.REPORT_BASIS_SCOPE,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
        <div style="display:none;margin:0 0 18pt 0;padding:0;">
            <div style="display: inline-block;background-color: #28a745;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.AboutReport.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #28a745;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.AboutReport.label', language)}</h3>
          </div>
        </div>
         <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.ReportBasisScope.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.ReportBasisScopeContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.AboutReport.label', language),
      index: 3,
      sortOrder: 4,
      type: ReportSection.ABOUT_REPORT,
      pages: pages
    }
  }

  static getOrganizationalProfileSection(b3Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);

    let html = '';
    if (b3Data.length > 0 && b3Data.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b3Data, normalizedLang, getTranslation(i18n, 'GHGReport.OrganizationalProfileBoundaries.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.OrganisationalStructureBoundaries.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.ORGANISATIONAL_STRUCTURE_BOUNDARIES,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
        <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #dc3545;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.OrganizationalProfileBoundaries.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #dc3545;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.OrganizationalProfileBoundaries.label', language)}</h3>
          </div>
        </div>
         <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.OrganisationalStructureBoundaries.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.OrganisationalStructureBoundariesContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.OrganizationalProfileBoundaries.label', language),
      index: 4,
      sortOrder: 5,
      type: ReportSection.ORGANIZATIONAL_PROFILE,
      pages: pages
    }
  }

  static getBaseYearMethodologySection(b4Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);

    let html = '';
    if (b4Data.length > 0 && b4Data.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b4Data, normalizedLang, getTranslation(i18n, 'GHGReport.BaseYearMethodology.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.ReportingBaseYearApproach.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.REPORTING_BASE_YEAR_APPROACH,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
        <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #6f42c1;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.BaseYearMethodology.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #6f42c1;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.BaseYearMethodology.label', language)}</h3>
          </div>
        </div>
         <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.ReportingBaseYearApproach.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.ReportingBaseYearApproachContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.BaseYearMethodology.label', language),
      index: 5,
      sortOrder: 6,
      type: ReportSection.BASE_YEAR_METHODOLOGY,
      pages: pages
    }
  }

  static getGHGEmissionsInventorySection(b5Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    let html = '';
    if (b5Data.length > 0 && b5Data.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b5Data, normalizedLang, getTranslation(i18n, 'GHGReport.GHGEmissionsInventory.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.EmissionsAccounting.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.EMISSIONS_ACCOUNTING,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
          <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #fd7e14;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.GHGEmissionsInventory.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #fd7e14;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.GHGEmissionsInventory.label', language)}</h3>
          </div>
        </div>
         <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.EmissionsAccounting.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.EmissionsAccountingContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.GHGEmissionsInventory.label', language),
      index: 6,
      sortOrder: 7,
      type: ReportSection.GHG_EMISSIONS_INVENTORY,
      pages: pages
    }
  }

  static getEnergyUseEmissionSourcesSection(b6Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);

    let html = '';
    if (b6Data.length > 0 && b6Data.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b6Data, normalizedLang, getTranslation(i18n, 'GHGReport.EnergyUseEmissionSources.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.EnergyMixEmissionSources.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.ENERGY_MIX_EMISSION_SOURCES,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
        <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #20c997;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.EnergyUseEmissionSources.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #20c997;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.EnergyUseEmissionSources.label', language)}</h3>
          </div>
        </div>
         <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.EnergyMixEmissionSources.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.EnergyMixEmissionSourcesContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.EnergyUseEmissionSources.label', language),
      index: 7,
      sortOrder: 8,
      type: ReportSection.ENERGY_USE_EMISSION_SOURCES,
      pages: pages
    }
  }

  static getGHGReductionStrategySection(b7Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    let html = '';
    if (b7Data.length > 0 && b7Data.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b7Data, normalizedLang, getTranslation(i18n, 'GHGReport.GHGReductionStrategyPerformanceIndicators.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.GHGManagementPerformance.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.GHG_MANAGEMENT_PERFORMANCE,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
        <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #e83e8c;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.GHGReductionStrategyPerformanceIndicators.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #e83e8c;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.GHGReductionStrategyPerformanceIndicators.label', language)}</h3>
          </div>
        </div>
         <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.GHGManagementPerformance.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.GHGManagementPerformanceContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.GHGReductionStrategyPerformanceIndicators.label', language),
      index: 8,
      sortOrder: 9,
      type: ReportSection.GHG_REDUCTION_STRATEGY,
      pages: pages
    }
  }

  static getVerificationAssuranceSection(b8Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    let html = '';
    if (b8Data.length > 0 && b8Data.some(item => allowedTypes.includes(item.keyValueType) && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b8Data, normalizedLang, getTranslation(i18n, 'GHGReport.VerificationAssuranceDataQuality.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.VerificationInventoryQuality.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.VERIFICATION_INVENTORY_QUALITY,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
        <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #6c757d;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.VerificationAssuranceDataQuality.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">   
          <div style="display: inline-block;background-color: #6c757d;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.VerificationAssuranceDataQuality.label', language)}</h3>
          </div>
        </div>
         <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.VerificationInventoryQuality.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.VerificationInventoryQualityContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.VerificationAssuranceDataQuality.label', language),
      index: 9,
      sortOrder: 10,
      type: ReportSection.VERIFICATION_ASSURANCE,
      pages: pages
    }
  }

  static getInsightsSection(insightsData, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.Insights.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.INSIGHTS,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
        <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #8e44ad;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.Insights.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;"> 
          <div style="display: inline-block;background-color: #8e44ad;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.Insights.label', language)}</h3>
          </div>
        </div>
        <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.InsightsContent.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.InsightsDescription.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>`,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.Insights.label', language),
      index: 9,
      sortOrder: 10,
      type: ReportSection.INSIGHTS,
      pages: pages
    }
  }

  static getAnnexuresSection(b9Data, language, t, i18n, settings) {
    // Normalize the language code
    const normalizedLang = normalizeLanguageCode(language);
    let html = '';
    if (b9Data.length > 0
      && b9Data.some(item => allowedTypes.includes(item.keyValueType)
        && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)))) {
      html = pageHtml(b9Data, normalizedLang, getTranslation(i18n, 'GHGReport.Annexures.label', language), settings);
    }
    const pages = [{
      name: getTranslation(i18n, 'GHGReport.ReferencesSupportingMaterials.label', language),
      index: 0,
      sortOrder: 1,
      type: ReportPageType.REFERENCES_SUPPORTING_MATERIALS,
      html: `<div style="font-family: Arial, sans-serif; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
        <div style="display:none;margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #17a2b8;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};max-width: fit-content;">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.Annexures.label', language)}</h3>
          </div>
        </div>
        <div style="margin:0 0 18pt 0;padding:0;">
          <div style="display: inline-block;background-color: #17a2b8;    padding: ${GHG_SECTION_HEAD_PILL_PADDING};
    border-radius: ${GHG_SECTION_HEAD_PILL_RADIUS};">
            <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;font-size: ${GHG_SECTION_HEAD_PILL_FONT};  font-weight: 700;">${getTranslation(i18n, 'GHGReport.Annexures.label', language)}</h3>
          </div>
        </div>
         <p style="font-size: 16px;"><span style="color: rgb(230, 126, 35);"><em>[ ${getTranslation(i18n, 'ReportGenText.label', language)} ]</em></span></p>
         <div style="padding:0;margin:0;">
           <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">${getTranslation(i18n, "GHGReport.ReferencesSupportingMaterials.label", language)}</h2>
           <p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'GHGReport.ReferencesSupportingMaterialsContent.label', language)}</p>
          <p style="font-size: 16px;"><span style="color: rgb(126, 140, 141);"><strong>[${getTranslation(i18n, 'ReportGenImageText.label', language)}]</strong></span></p>
          <p><img src="${BASE_URL_DOMAIN}/report/Report_image1.webp" alt="" width="100%" height="200"></p>
        </div>
      </div> <br>` + html,
    }]

    return {
      name: getTranslation(i18n, 'GHGReport.Annexures.label', language),
      index: 10,
      sortOrder: 11,
      type: ReportSection.ANNEXURES,
      pages: pages
    }
  }

  static getBackCover(companyId, year, t) {
    const logoURL = API_ROUTES.PROFILESETTINGS.GETCOMPANYLOGO.replace(
      "<companyId>",
      companyId
    );
    return {
      name: t("Root.label"),
      index: 10,
      sortOrder: 11,
      type: ReportSection.ROOT,
      pages: [
        {
          name: t("BackCover.key0"),
          sortOrder: 0,
          index: 1,
          type: ReportPageType.ROOT,
          html: `<div style='position: relative; background-color: #f8fafc; page-break-after: always; min-height: 11.69in; padding: 6rem 5rem; box-sizing: border-box;'>
            <div style='position: absolute; top: 40px; left: 40px; width: 140px;'>
              <img src=${logoURL} alt='Company Logo' style='width: 100%;' />
            </div>
            <div>
              <h3 style='font-size: 60px; font-weight: 800; color: #0a58ca; margin-bottom: 0px;'>${t("GHGReport.GHGFull.label")} ${year}</h3>
              <h4 style='font-size: 40px; font-weight: 700; color: #091a2a;'>${t("ReportingStandards.label")} <br /> (${t("GHG.label")})</h4>
          </div>
        </div>`,
        },
      ]
    };
  }
}
